import React from 'react';
import './StoreCard.css';

function StoreCard({ 
  route, 
  isFlipped, 
  showBackContent, 
  isPriceAvailable, 
  onFlip, 
  onPriceClick,
  onInauguralClick 
}) {
  
  const formatBulletPoint = (point) => {
    // Handle both string (legacy) and object formats
    if (typeof point === 'string') {
      return { mainText: point, subText: '' };
    }
    return {
      mainText: point?.mainText || "",
      subText: point?.subText || ""
    };
  };

  const handleInauguralRibbonClick = (e) => {
    e.stopPropagation();
    onInauguralClick();
  };

  // Get card colors with 3-color semantic system
  // Support both old (11-color) and new (3-color) format
  const cardColors = route.cardColors || {};

  // New 3-color system (semantic)
  const primaryColor = cardColors.primaryColor || '#FFD700';      // Gold - accent/theme
  const secondaryColor = cardColors.secondaryColor || '#FFFFFF';  // White - text/borders
  const backgroundColor = cardColors.backgroundColor || '#072231'; // Navy - background

  // Map to all UI elements
  const mappedColors = {
    // Card structure
    background: backgroundColor,
    borderColor: secondaryColor,

    // Interactive elements
    flipButtonColor: secondaryColor,
    buttonGradientStart: primaryColor,
    buttonGradientEnd: primaryColor, // Solid color now
    buttonTextColor: backgroundColor,

    // Front card text
    titleColor: secondaryColor,
    taglineColor: primaryColor,

    // Back card bullets
    bulletDiamondColor: primaryColor,
    bulletMainTextColor: primaryColor,
    bulletSubTextColor: secondaryColor
  };

  // Get background image settings
  const backgroundImage = route.cardBackgroundImage || null;
  const overlayColor = route.cardOverlayColor || 'rgba(0, 0, 0, 0.4)';
  const overlayOpacity = route.cardOverlayOpacity !== undefined ? route.cardOverlayOpacity : 0.4;

  // Build card face styles
  const getCardFaceStyles = () => {
    const styles = {
      borderColor: '#FFFFFF' // Always white border
    };

    if (backgroundImage) {
      // Use background image if available
      styles.backgroundImage = `url(${backgroundImage})`;
      styles.backgroundColor = mappedColors.background; // Fallback color
    } else {
      // Use solid background color
      styles.background = mappedColors.background;
    }

    return styles;
  };

  // Build overlay styles for pseudo-element
  const overlayStyles = backgroundImage && overlayColor ? {
    '--overlay-color': overlayColor,
    '--overlay-opacity': overlayOpacity
  } : {};

  // Dynamic Card Face Components
  const CardFront = () => (
    <div
      className={`pass-card-face pass-card-front ${backgroundImage ? 'has-background-image' : ''}`}
      style={{
        ...getCardFaceStyles(),
        ...overlayStyles
      }}
    >
      {/* Beta Ribbon - Only on front */}
      {route.storeStatus === 'beta' && (
        <div
          className="inaugural-ribbon"
          onClick={handleInauguralRibbonClick}
          style={{
            background: primaryColor,
            color: backgroundColor
          }}
        >
          <span>INAUGURAL</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <circle cx="12" cy="8" r="1" fill="currentColor"/>
            <path d="M12 11V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
      )}

      {/* Flip button - Only on front */}
      <button
        className="flip-button"
        onClick={onFlip}
        aria-label="Flip card"
        style={{ color: mappedColors.flipButtonColor }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" fill="currentColor"/>
        </svg>
      </button>

      {/* Coming Soon Ribbon - Only show if coming-soon */}
      {route.storeStatus === 'coming-soon' && (
        <div
          className="coming-soon-ribbon"
          style={{
            background: '#DC3545',  // Red color
            color: '#FFFFFF'        // White text
          }}
        >
          <span>COMING SOON</span>
        </div>
      )}

      {/* Route Name */}
      <h2 className="route-name" style={{ color: mappedColors.titleColor }}>{route.name}</h2>

      {/* Tagline */}
      {route.storeTagline && (
        <p className="route-tagline" style={{ color: mappedColors.taglineColor }}>{route.storeTagline}</p>
      )}

      {/* Route Image */}
      <div className="route-image-container" style={{ backgroundColor: mappedColors.background }}>
        {route.storeCoverImage ? (
          <img
            src={route.storeCoverImage}
            alt={route.name}
            className="route-image"
          />
        ) : (
          <div className="route-image-placeholder">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7V12C2 16.5 4.5 20.7 8 22.3C9.1 22.8 10.1 23 11 23H13C13.9 23 14.9 22.8 16 22.3C19.5 20.7 22 16.5 22 12V7L12 2Z" stroke="#FFD700" strokeWidth="2"/>
              <circle cx="12" cy="12" r="3" stroke="#FFD700" strokeWidth="2"/>
            </svg>
          </div>
        )}
      </div>

      {/* Go Button / Reserve Button */}
      <button
        className={`price-button ${!isPriceAvailable ? 'disabled' : ''}`}
        onClick={() => isPriceAvailable ? onPriceClick(route) : onPriceClick(route)}
        disabled={false}
        style={{
          background: isPriceAvailable ? primaryColor : '#DC3545',
          color: isPriceAvailable ? backgroundColor : '#FFFFFF'
        }}
      >
        {isPriceAvailable ? 'Go' : <span className="coming-soon-text">Reserve</span>}
      </button>
    </div>
  );

  const CardBack = () => (
    <div
      className={`pass-card-face pass-card-back ${backgroundImage ? 'has-background-image' : ''}`}
      style={{
        ...getCardFaceStyles(),
        ...overlayStyles
      }}
    >
      {/* Flip button - back version */}
      <button
        className="flip-button-back"
        onClick={onFlip}
        aria-label="Flip card back"
        style={{ color: mappedColors.flipButtonColor }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" fill="currentColor"/>
        </svg>
      </button>

      <div className="bullet-points">
        {route.storeBulletPoints?.map((point, index) => {
          const formattedPoint = formatBulletPoint(point);
          return (
            <div key={index} className="bullet-point">
              <span className="bullet-diamond" style={{ color: mappedColors.bulletDiamondColor }}>◆</span>
              <div className="bullet-content">
                <div className="bullet-main-text" style={{ color: mappedColors.bulletMainTextColor }}>
                  {formattedPoint.mainText}
                </div>
                {formattedPoint.subText && (
                  <div className="bullet-sub-text" style={{ color: mappedColors.bulletSubTextColor }}>
                    {formattedPoint.subText}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Go Button / Reserve Button on back */}
      <button
        className={`price-button ${!isPriceAvailable ? 'disabled' : ''}`}
        onClick={() => isPriceAvailable ? onPriceClick(route) : onPriceClick(route)}
        disabled={false}
        style={{
          background: isPriceAvailable ? primaryColor : '#DC3545',
          color: isPriceAvailable ? backgroundColor : '#FFFFFF'
        }}
      >
        {isPriceAvailable ? 'Go' : <span className="coming-soon-text">Reserve</span>}
      </button>
    </div>
  );

  return (
    <div className="pass-card-container">
      <div className={`pass-card ${isFlipped ? 'flipped' : ''}`}>
        {/* Use delayed state for content, immediate state for CSS animation */}
        {!showBackContent ? <CardFront /> : <CardBack />}
      </div>
    </div>
  );
}

export default StoreCard;